import React from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Komponen Sidebar untuk navigasi desktop
 * @param {boolean} isOpen - Status sidebar terbuka/tertutup
 * @param {boolean} isDarkMode - Status dark mode
 * @param {Array} menuItems - Array menu items
 * @param {string} activeMenu - ID menu yang aktif
 * @param {Array} expandedMenus - Array ID menu yang expanded
 * @param {function} onMenuClick - Callback saat menu diklik
 * @param {function} onSubMenuClick - Callback saat submenu diklik
 */
const Sidebar = ({
    isOpen,
    isDarkMode,
    menuItems,
    activeMenu,
    expandedMenus,
    onMenuClick,
    onSubMenuClick
}) => {
    /**
     * Mendapatkan class untuk menu item
     */
    const getMenuClass = (id) => `
        flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer text-xs font-medium transition-all mb-1 
        ${activeMenu === id
            ? (isDarkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600')
            : (isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')
        }
    `;

    /**
     * Mendapatkan class untuk parent menu item
     */
    const getParentMenuClass = (item) => `
        flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-xs font-medium transition-all mb-1 
        ${(!item.subItems && activeMenu === item.id)
            ? (isDarkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600')
            : (isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')
        }
    `;

    return (
        <aside className={`
            hidden md:flex flex-shrink-0 border-r transition-all duration-300 ease-in-out flex-col 
            ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} 
            ${isOpen ? 'w-56' : 'w-16'}
        `}>
            {/* Logo */}
            <div className={`h-14 flex items-center justify-center border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2 overflow-hidden px-4">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 rounded flex items-center justify-center text-white font-bold text-xs">
                        C
                    </div>
                    <span className={`font-bold text-base whitespace-nowrap transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 w-0'}`}>
                        Cloudnine
                    </span>
                </div>
            </div>

            {/* Menu List */}
            <div className="flex-1 overflow-y-auto p-3">
                {menuItems.map(item => (
                    <div key={item.id}>
                        {/* Parent Menu Item */}
                        <div
                            onClick={() => onMenuClick(item)}
                            className={getParentMenuClass(item)}
                        >
                            <div className="flex items-center gap-3">
                                <item.icon size={16} />
                                <span className={`${isOpen ? 'block' : 'hidden'}`}>
                                    {item.label}
                                </span>
                            </div>
                            {item.subItems && isOpen && (
                                <ChevronDown
                                    size={14}
                                    className={`transition-transform duration-200 ${expandedMenus.includes(item.id) ? 'rotate-180' : ''}`}
                                />
                            )}
                        </div>

                        {/* Sub Menu Items */}
                        {item.subItems && (
                            <div className={`
                                overflow-hidden transition-all duration-300 ease-in-out 
                                ${expandedMenus.includes(item.id) && isOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}
                            `}>
                                <div className={`ml-4 pl-4 border-l mt-1 space-y-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                    {item.subItems.map(sub => (
                                        <div
                                            key={sub.id}
                                            onClick={() => onSubMenuClick(sub.id)}
                                            className={getMenuClass(sub.id)}
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>
                                            <span>{sub.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className={`p-3 border-t text-[10px] text-center text-slate-400 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                {isOpen ? 'v1.0.0' : 'v1'}
            </div>
        </aside>
    );
};

export default Sidebar;
